import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { TableRow, TableCell } from '@material-ui/core';
import { DeviceSelectItem, MeasureSelectItem, ItemSelectItem } from '../../../function/GroupDataModel';
import ItemComboBox, { ComboBoxItemType } from '../../atoms/ComboBox/ItemComboBox';
import { useMemo } from 'react';

type DeviceMeasItemProps = {
  selectList: DeviceSelectItem[];
  plcNo: number;
  setNo: number;
  kind: number;
  plcSetFunc: (plc: number, set: number, kind: number) => void;
};

const useStyles = makeStyles(
  createStyles({
    tableStyle: {
      width: '400px',
      borderStyle: 'none',
    },
    rowStyle: {
      borderStyle: 'none',
    },
    cellStyle: {
      borderStyle: 'none',
    },
    cellName: {
      width: '140px',
    },
    cellData: {
      width: '220px',
    },
    selectNo: {
      width: '120px',
    },
    selectName: {
      width: '200px',
    },
    dropdownStyle: {
      maxHeight: 300,
    },
    gridStyle: {
      borderStyle: 'solid',
    },
    tableItem: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      orderBottomStyle: 'inset',
      borderLeftStyle: 'solid',
      borderRightStyle: 'solid',
      textAlign: 'center',
      minWidth: '80px',
      height: '8px',
      padding: '1px',
    },
  })
);

/**
 * コンボボックス情報取得(設定からの取得)
 * @param devsel 機器選択情報一式
 * @param plc PLC番号
 * @param no セット番号
 * @param kind 種類番号
 * @returns
 * @description PLC番号、セット番号、種類番号からコンボボックスの情報を取得します。
 * PLC番号、セット番号、種類番号が決まっている状態から適切なコンボボックスを選択します。
 */
const getComboInfoFromSetting = (
  devsel: DeviceSelectItem[],
  plc: number,
  no: number,
  kind: number
): [number, number, MeasureSelectItem[], number, ItemSelectItem[]] => {
  return getDevMeasItmIdxCore(
    devsel,
    plc,
    no,
    kind,
    (ms: MeasureSelectItem, idx: number): [boolean, number, ItemSelectItem[]] => {
      const iidx = ms.ItemSelects.findIndex((is) => {
        return is.PLC_NO === plc && is.SET_NO === no && is.KIND === kind;
      });
      if (iidx !== -1) {
        return [true, iidx, ms.ItemSelects];
      } else {
        return [false, -1, []];
      }
    }
  );
};
/**
 * コンボボックス情報取得(機器切り替え時)
 * @param devsel コンボボックス情報取得
 * @param devIdx 機器インデックス
 * @returns
 * @description 機器情報を切り替えた際のデフォルトの選択肢を取得します。
 */
const getComboInfoOnDeviceChange = (
  devsel: DeviceSelectItem[],
  devIdx: number
): [number, number, MeasureSelectItem[], number, ItemSelectItem[]] => {
  const targetDev = devsel.find((ds) => ds.DEV_INDEX === devIdx);
  if (targetDev) {
    return getDevMeasItmIdxCore(
      [targetDev],
      0,
      0,
      0,
      (ms: MeasureSelectItem, idx: number): [boolean, number, ItemSelectItem[]] => {
        // 無条件で最初の計測を選択
        return [true, 0, ms.ItemSelects];
      }
    );
  }
  return [-1, 0, [], 0, []];
};
/**
 * コンボボックス情報取得(計測切り替え時)
 * @param device 対象機器
 * @param measIdx 計測インデックス
 * @returns
 * @description 計測を切り替えた際のデフォルトの選択肢を取得します。
 */
const getComboInfoOnMeasChange = (
  device: DeviceSelectItem,
  measIdx: number
): [number, number, MeasureSelectItem[], number, ItemSelectItem[]] => {
  return getDevMeasItmIdxCore(
    [device],
    0,
    0,
    0,
    (ms: MeasureSelectItem, idx: number): [boolean, number, ItemSelectItem[]] => {
      if (idx === measIdx) {
        // 指定の測定インデックスの先頭を選択
        return [true, 0, ms.ItemSelects];
      } else {
        return [false, -1, []];
      }
    }
  );
};
/**
 *
 * @param devsel 機器選択情報一式
 * @param plc PLC番号
 * @param no セット番号
 * @param kind 種類番号
 * @param func 項目選択処理
 * @returns [機器インデックス,計測インデックス,計測選択肢,項目インデックス,項目選択肢]
 */
const getDevMeasItmIdxCore = (
  devsel: DeviceSelectItem[],
  plc: number,
  no: number,
  kind: number,
  func: (ms: MeasureSelectItem, idx: number) => [boolean, number, ItemSelectItem[]]
): [number, number, MeasureSelectItem[], number, ItemSelectItem[]] => {
  let mno = -1,
    ino = -1;
  let mlist: MeasureSelectItem[] = [];
  let ilist: ItemSelectItem[] = [];
  const dno = devsel.findIndex((ds) => {
    const midx = ds.MeasureSelects.findIndex((ms, idx) => {
      let ires = false;
      [ires, ino, ilist] = func(ms, idx);
      return ires;
    });
    if (midx !== -1) {
      mno = midx;
      mlist = ds.MeasureSelects;
      return true;
    } else {
      return false;
    }
  });

  return [dno, mno, mlist, ino, ilist];
};

const DeviceMeasItem: React.FC<DeviceMeasItemProps> = (props: DeviceMeasItemProps) => {
  const classes = useStyles();
  const [devIdx, setDevIdx] = useState<number>(0);
  const [measCombos, setMeasCombos] = useState<ComboBoxItemType[]>([]);
  const [measIdx, setMeasIdx] = useState<number>(0);
  const [itemCombos, setItemCombos] = useState<ComboBoxItemType[]>([]);
  const [itemIdx, setItemIdx] = useState<number>(0);

  console.log(['-- DeviceMeasItem --', measCombos]);

  // 機器選択用の選択肢作成
  const devCombos = useMemo(() => {
    const selects = props.selectList.map<ComboBoxItemType>((sel, idx) => {
      return { index: idx + 1, name: sel.DEV_NO };
    });
    selects.unshift({ index: 0, name: '---' });

    return selects;
  }, [props.selectList]);

  useEffect(() => {
    const [dno, mno, mlst, ino, ilst] = getComboInfoFromSetting(props.selectList, props.plcNo, props.setNo, props.kind);

    setDevIdx(dno + 1);
    const meas = mlst.map((m, idx) => {
      return { index: idx, name: m.MES_NAME };
    });
    setMeasCombos(meas);
    setMeasIdx(mno);
    const item = ilst.map((i, idx) => {
      return { index: idx, name: i.ITM_NAME };
    });
    setItemCombos(item);
    setItemIdx(ino);
  }, [props.plcNo, props.setNo, props.kind, props.selectList]);

  const deviceChange = (idx: number) => {
    if (idx > 0 && props.selectList.length > idx - 1) {
      const [dno1, mno1, mlist, ino1, ilist] = getComboInfoOnDeviceChange(
        props.selectList,
        props.selectList[idx - 1].DEV_INDEX
      );
      if (ilist.length > 0) {
        setDevIdx(idx);
        const meas = mlist.map((sm, sidx) => {
          return { index: sidx, name: sm.MES_NAME };
        });
        setMeasCombos(meas);
        setMeasIdx(mno1);
        const item = ilist.map((si, sidx2) => {
          return { index: sidx2, name: si.ITM_NAME };
        });
        setItemCombos(item);
        setItemIdx(ino1);

        props.plcSetFunc(ilist[0].PLC_NO, ilist[0].SET_NO, ilist[0].KIND);
        return;
      }
    }
    props.plcSetFunc(0, 0, 0);
  };
  const measureChange = (idx: number, plcidx: number) => {
    if (plcidx > 0 && props.selectList.length > plcidx - 1) {
      const ds = props.selectList[plcidx - 1];
      const [dno, mno, mlist, ino, ilist] = getComboInfoOnMeasChange(ds, idx);

      if (ilist.length > 0) {
        props.plcSetFunc(ilist[0].PLC_NO, ilist[0].SET_NO, ilist[0].KIND);
        return;
      }
    }
    props.plcSetFunc(0, 0, 0);
  };
  const itemChange = (idx: number, plcidx: number, measidx: number) => {
    if (plcidx > 0 && props.selectList.length > plcidx - 1) {
      const ds = props.selectList[plcidx - 1];
      const ms = ds.MeasureSelects[measidx];

      //setItemIdx(idx);
      props.plcSetFunc(ms.ItemSelects[idx].PLC_NO, ms.ItemSelects[idx].SET_NO, ms.ItemSelects[idx].KIND);
      return;
    }
    props.plcSetFunc(0, 0, 0);
  };

  return (
    <TableRow>
      <TableCell className={classes.tableItem}>
        <ItemComboBox
          itemList={devCombos}
          label=""
          value={devIdx}
          onChangeEvent={(e) => {
            if (typeof e.target.value === 'string') {
              deviceChange(parseInt(e.target.value));
            }
          }}
        />
      </TableCell>
      <TableCell className={classes.tableItem}>
        <ItemComboBox
          itemList={measCombos}
          label=""
          value={measIdx}
          onChangeEvent={(e) => {
            if (typeof e.target.value === 'string') {
              measureChange(parseInt(e.target.value), devIdx);
            }
          }}
        />
      </TableCell>
      <TableCell className={classes.tableItem}>
        <ItemComboBox
          itemList={itemCombos}
          label=""
          value={itemIdx}
          onChangeEvent={(e) => {
            if (typeof e.target.value === 'string') {
              itemChange(parseInt(e.target.value), devIdx, measIdx);
            }
          }}
        />
      </TableCell>
    </TableRow>
  );
};

export default DeviceMeasItem;
